import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LogOut, ClipboardList, ScanLine, History, Volume2, VolumeX, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { HeaderLogo } from "@/components/Brand";
import { Spinner } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { staffApi, errMsg } from "@/lib/api";
import { unlockAudio, play, soundReady, getVolume } from "@/lib/sound";
import { useLive, useWakeLock } from "@/lib/realtime";
import OrderBoard from "./OrderBoard";
import DoorScanner from "./DoorScanner";
import SoundSettings from "./SoundSettings";

export const staffKey = (slug) => `lg_staff_${slug}`;

const Login = ({ slug, onLogin }) => {
  const [info, setInfo] = useState(null);
  const [contact, setContact] = useState("");
  const [secret, setSecret] = useState("");
  useEffect(() => { staffApi().get(`/venues/${slug}`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); }, [slug]);
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await staffApi().post("/login", { slug, contact, secret }); onLogin(r.data.token); }
    catch (err) { toast.error(errMsg(err)); setSecret(""); }
  };
  if (!info) return <Spinner />;
  return (
    <form onSubmit={submit} className="mx-auto max-w-sm px-4 py-10" data-testid="staff-login">
      <div className="eyebrow">LineGone staff sign-in</div>
      <h1 className="mt-1 text-3xl font-black">{info.name}</h1>
      <label className="mt-6 block text-sm font-semibold">Email or mobile phone</label>
      <Input className="mt-1" value={contact} onChange={(e) => setContact(e.target.value)} autoComplete="username" data-testid="staff-login-contact-input" />
      <label className="mt-4 block text-sm font-semibold">Password or PIN</label>
      <Input className="mt-1" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="current-password" data-testid="staff-login-secret-input" />
      <button className="btn-primary mt-6 w-full" disabled={contact.length < 3 || secret.length < 4} data-testid="staff-login-btn">Sign in</button>
      <p className="mt-6 text-center text-sm text-muted2">New here? <Link to={`/staff/join?venue=${slug}`} className="link-lg" data-testid="staff-join-link">Join with an invite code</Link></p>
    </form>
  );
};

const SoundGate = ({ onOn }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand p-6" data-testid="sound-gate">
    <button onClick={() => { unlockAudio(); play("new_order"); onOn(); }} data-testid="sound-gate-btn"
      className="flex w-full max-w-md flex-col items-center gap-4 rounded-[2rem] bg-white px-8 py-14 text-ink shadow-2xl transition-transform active:scale-95">
      <Volume2 size={64} className="text-brand" />
      <span className="font-heading text-3xl font-black sm:text-4xl">Tap to turn on order sounds</span>
      <span className="text-sm text-muted2">You'll hear a test sound. Orders stay hidden until sound is on.</span>
    </button>
  </div>
);

function useSoundHealth(on) {
  const [ok, setOk] = useState(true);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setOk(soundReady() && getVolume() > 0), 2000);
    return () => clearInterval(t);
  }, [on]);
  return ok;
}

const SoundWarning = () => (
  <div className="flex flex-wrap items-center justify-between gap-2 bg-danger px-4 py-3 font-bold text-white" data-testid="sound-warning-banner">
    <span className="flex items-center gap-2"><VolumeX size={18} /> Order sounds are off or muted. Check the device volume.</span>
    <button className="rounded-full bg-white px-4 py-1.5 text-sm text-danger" onClick={() => { unlockAudio(); play("new_order"); }} data-testid="sound-warning-fix-btn">Turn sound back on</button>
  </div>
);

const ActivityLog = ({ api }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/activity").then((r) => setRows(r.data)).catch(() => {}); }, [api]);
  return (
    <ul className="card-lg mx-auto max-w-3xl divide-y divide-line" data-testid="activity-log">
      {rows.map((a) => <li key={a.id} className="flex flex-wrap justify-between gap-2 p-3 text-sm" data-testid="activity-row">
        <span><b>{a.staff}</b> {a.action.replace("_", " ")} <b>{a.ref}</b> {a.detail && <span className="text-muted2">— {a.detail}</span>}</span>
        <span className="text-xs text-muted2">{new Date(a.at).toLocaleString("en-CA")}</span></li>)}
      {rows.length === 0 && <li className="p-4 text-muted2">No activity yet.</li>}
    </ul>
  );
};

const TABS = [["board", "Orders", ClipboardList], ["scan", "Door scanner", ScanLine], ["log", "Activity", History], ["sound", "Sound", Volume2]];

const WakeBadge = ({ state }) => (
  <span className={`hidden items-center gap-1 rounded-full px-3 py-1 text-xs font-bold sm:flex ${state === "on" ? "bg-brand-pale text-brand" : "bg-danger-pale text-danger"}`} data-testid="wake-lock-status" data-state={state}>
    {state === "on" ? <><Sun size={14} /> Screen stays awake</> : <><Moon size={14} /> Screen may sleep</>}
  </span>
);

export default function StaffScreen() {
  const { slug } = useParams();
  const [token, setToken] = useState(localStorage.getItem(staffKey(slug)));
  const [tab, setTab] = useState("board");
  const [soundOn, setSoundOn] = useState(false);
  const [me, setMe] = useState(null);
  const [tick, setTick] = useState(0);
  const api = useMemo(() => staffApi(token), [token]);
  const soundOk = useSoundHealth(soundOn);
  const wake = useWakeLock(!!token && soundOn && tab === "board");
  useEffect(() => { document.title = "Staff screen · LineGone"; }, []);
  const logout = (msg) => { localStorage.removeItem(staffKey(slug)); setToken(null); setMe(null); setSoundOn(false); if (msg) toast.error(msg); };
  useEffect(() => {
    if (!token) return;
    api.get("/me").then((r) => setMe(r.data)).catch((e) => { if (e?.response?.status === 401) logout(errMsg(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const connected = useLive(token ? `staff?token=${encodeURIComponent(token)}` : null, (m) => {
    if (m.type === "logout") logout("You were signed out.");
    else setTick((t) => t + 1);
  });
  const login = (t) => { localStorage.setItem(staffKey(slug), t); setToken(t); };
  if (!token) return <div className="min-h-screen bg-white"><header className="flex h-14 items-center border-b border-line px-4"><HeaderLogo className="h-6" to={`/staff/${slug}`} /></header><Login slug={slug} onLogin={login} /></div>;
  return (
    <div className="min-h-screen bg-white" data-testid="staff-screen">
      {!soundOn && <SoundGate onOn={() => setSoundOn(true)} />}
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-line bg-white px-4">
        <HeaderLogo className="h-6" to={`/staff/${slug}`} />
        <div className="flex items-center gap-2">
          <WakeBadge state={wake} />
          <span className={`h-2.5 w-2.5 rounded-full ${connected ? "bg-brand" : "bg-amber-400"}`} title={connected ? "Live" : "Reconnecting"} data-testid="live-indicator" data-live={connected} />
          {me?.kind === "owner" && <Link to="/dashboard" className="btn-secondary whitespace-nowrap !px-3 !py-1.5 text-sm" data-testid="staff-back-dashboard-link">Dashboard</Link>}
          <span className="max-w-[90px] truncate text-sm font-semibold sm:max-w-none" data-testid="staff-current-name">{me?.name}{me?.kind === "owner" ? " (owner)" : ""}</span>
          <button className="rounded-full bg-brand-pale p-2" onClick={() => logout()} aria-label="Sign out" data-testid="staff-logout-btn"><LogOut size={18} /></button>
        </div>
      </header>
      {soundOn && !soundOk && <SoundWarning />}
      {soundOn && <>
        <nav className="flex gap-2 overflow-x-auto px-4 pt-3">
          {TABS.map(([k, l, I]) => <button key={k} onClick={() => setTab(k)} data-testid={`staff-tab-${k}`}
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 font-bold ${tab === k ? "bg-brand text-white" : "bg-brand-pale"}`}><I size={16} /> {l}</button>)}
        </nav>
        <div className="p-4">
          {tab === "board" && <OrderBoard api={api} onAuthFail={logout} tick={tick} connected={connected} />}
          {tab === "scan" && <DoorScanner api={api} />}
          {tab === "log" && <ActivityLog api={api} />}
          {tab === "sound" && <SoundSettings />}
        </div>
      </>}
    </div>
  );
}
